<template>
    <!--
        One action Milo proposed (docs/API.md §14.6): tv-api's own sentence, the state, and while
        it is pending the two buttons. Nothing happens until Potrdi. A reply to a guest's feedback
        also shows its whole text, exactly as it will be sent, its language and the status after;
        an event for Okolica its picture and details (MiloLocalEventDetails); decorations on the
        home tiles' pictures the tiles and what is left alone.
    -->
    <div class="flex flex-col gap-2 rounded-md border border-default bg-default p-3">
        <div class="flex items-start gap-2">
            <UIcon :name="icon" class="mt-0.5 size-4 shrink-0 text-primary" />
            <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                <p v-if="feedbackLabel" class="text-xs font-medium text-muted">{{ feedbackLabel }}</p>
                <p class="text-sm text-highlighted">{{ proposal.summary }}</p>
            </div>
            <AppTag :item="tag" />
        </div>

        <!-- A check-in where the PMS also reports a stay (§14.6 check_in `pms_conflict`). -->
        <p v-if="proposal.details.pms_conflict === true" class="text-xs text-warning">{{ $t('milo.pms_conflict') }}</p>

        <template v-if="isFeedback">
            <blockquote v-if="text" class="rounded-md border-l-2 border-primary bg-elevated px-3 py-2 text-sm whitespace-pre-line text-default">{{ text }}</blockquote>
            <div v-if="note" class="flex flex-col gap-0.5">
                <p class="text-xs text-muted">{{ $t('milo.proposal_note') }}</p>
                <p class="text-sm whitespace-pre-line text-default">{{ note }}</p>
            </div>
            <p class="flex flex-wrap gap-x-3 text-xs text-muted">
                <span v-if="language">{{ $t('milo.proposal_reply_language', { language }) }}</span>
                <span v-if="status">{{ isReply ? $t('milo.proposal_then_status', { status }) : $t('milo.proposal_new_status', { status }) }}</span>
            </p>
            <p v-if="isReply && pending" class="text-xs text-muted">{{ $t('milo.proposal_reply_hint') }}</p>
        </template>

        <!-- An event for Okolica (§14.6.1): its picture, details and source. -->
        <MiloLocalEventDetails
            v-if="proposal.kind === MiloProposalKind.LocalEvent"
            :proposal="proposal"
            :pending="pending"
            :busy="busy"
            :changing="changing ?? ''"
            @picture="(action) => emit('picture', proposal, action)"
        />

        <!-- An event to take off Okolica (§14.6.1): name, when, the past mark and a note. -->
        <div v-if="proposal.kind === MiloProposalKind.RemoveLocalEvent" class="flex flex-col gap-1">
            <div class="flex flex-wrap items-center gap-2">
                <p class="text-sm font-medium text-highlighted">{{ removeName }}</p>
                <span v-if="removePast" class="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning">{{ $t('milo.event_past') }}</span>
            </div>
            <p v-if="removeMeta" class="text-xs text-muted">{{ removeMeta }}</p>
            <p class="text-xs text-muted">{{ $t('milo.event_remove_note') }}</p>
        </div>

        <!-- Decorations on the home tiles' pictures (§14.6 seasonal_tiles): the tiles, a new theme's base, what is skipped. -->
        <div v-if="proposal.kind === MiloProposalKind.SeasonalTiles" class="flex flex-col gap-1" data-season-tiles-proposal>
            <div class="flex flex-wrap gap-1">
                <UBadge v-for="tile in seasonTiles" :key="tile.screen" :label="tile.title" color="neutral" variant="subtle" size="sm" icon="i-lucide-layout-grid" />
            </div>
            <p v-if="detail('base_name')" class="text-xs text-muted">{{ $t('milo.season_tiles_new', { base: detail('base_name') }) }}</p>
            <p v-if="seasonSkipped" class="text-xs text-muted">{{ $t('milo.season_tiles_skipped', { list: seasonSkipped }) }}</p>
            <p class="text-xs text-muted">{{ $t('milo.season_tiles_note') }}</p>
        </div>

        <p v-if="proposal.error" class="text-xs text-error">{{ proposal.error.message }}</p>

        <!-- Potrdi of an event translates it into the hotel's languages before it saves: a few seconds. -->
        <MiloWorking v-if="pending && translating" :text="$t('milo.event_translating')" />
        <div v-else-if="pending" class="flex justify-end gap-2">
            <UButton :label="$t('general.cancel')" color="neutral" variant="subtle" size="sm" :disabled="busy" @click="emit('decide', proposal, false)" />
            <UButton :label="$t('general.confirm')" icon="i-lucide-check" size="sm" :loading="busy" @click="confirm" />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import AppTag from '@/components/app/AppTag.vue'
    import MiloLocalEventDetails from '@/components/milo/MiloLocalEventDetails.vue'
    import MiloWorking from '@/components/milo/MiloWorking.vue'
    import { MiloPictureAction, MiloProposalKind } from '@/enums/milo'
    import { formatDay } from '@/libs/dayjs'
    import { languageLabel, pickText } from '@/libs/localized'
    import { proposalOpen, proposalTag } from '@/libs/miloChat'
    import type { LocalizedText, TagItem } from '@/types/Common'
    import type { IMiloProposal } from '@/types/Milo'

    const props = defineProps<{
        proposal: IMiloProposal
        busy?:    boolean
        /** Re-evaluated by the parent's clock so a card turns "expired" on its own. */
        now:      number
        /** A local event's picture action under way ('' or absent = none). */
        changing?: string
    }>()

    const emit = defineEmits<{
        decide:  [proposal: IMiloProposal, confirm: boolean]
        picture: [proposal: IMiloProposal, action: MiloPictureAction]
    }>()

    const { t, locale } = useI18n()

    const tag = computed((): TagItem => proposalTag(props.proposal, props.now))

    /** Potrdi pressed here and still running (busy also covers a cancel). */
    const confirming = ref(false)

    const confirm = () => {
        confirming.value = true
        emit('decide', props.proposal, true)
    }

    watch(() => props.busy, (busy) => {
        if (!busy) confirming.value = false
    })

    /** An Okolica event being confirmed: Milo translates and saves it (§14.6.1). */
    const translating = computed((): boolean => !!props.busy && confirming.value && props.proposal.kind === MiloProposalKind.LocalEvent)

    const pending = computed((): boolean => proposalOpen(props.proposal, props.now))

    const isReply = computed((): boolean => props.proposal.kind === MiloProposalKind.FeedbackReply)

    const isFeedback = computed((): boolean => isReply.value || props.proposal.kind === MiloProposalKind.FeedbackStatus)

    /** A detail of the proposal as text ('' when absent). */
    const detail = (key: string): string => {
        const value = props.proposal.details[key]

        return typeof value === 'string' ? value : ''
    }

    const feedbackLabel = computed((): string => {
        switch (props.proposal.kind) {
            case MiloProposalKind.FeedbackReply:     return t('milo.proposal_feedback_reply')
            case MiloProposalKind.FeedbackStatus:    return t('milo.proposal_feedback_status')
            case MiloProposalKind.LocalEvent:        return t('milo.proposal_local_event')
            case MiloProposalKind.RemoveLocalEvent:  return t('milo.proposal_remove_local_event')
            case MiloProposalKind.SeasonalTiles:     return t('milo.proposal_seasonal_tiles')
            default:                                 return ''
        }
    })

    /** A remove-event proposal's name (§14.6.1). */
    const removeName = computed((): string => pickText(props.proposal.details.name as LocalizedText | undefined, locale.value, 'sl'))

    /** Whether the event to remove is past. */
    const removePast = computed((): boolean => props.proposal.details.past === true)

    /** A remove-event proposal's when and where. */
    const removeMeta = computed((): string => {
        const day  = formatDay(detail('date'))
        const when = detail('time') ? t('milo.event_at', { day, time: detail('time') }) : day

        return [when, detail('venue')].filter(Boolean).join(' · ')
    })

    /** A seasonal tiles proposal's tile (§14.6 seasonal_tiles). */
    interface ISeasonTileRow {
        screen:  string
        title:   string
        reason?: string
    }

    /** The tiles a seasonal tiles proposal decorates. */
    const seasonTiles = computed((): ISeasonTileRow[] => (Array.isArray(props.proposal.details.tiles) ? props.proposal.details.tiles as ISeasonTileRow[] : []))

    /** The tiles it leaves alone, with why. */
    const seasonSkipped = computed((): string => {
        const skipped = Array.isArray(props.proposal.details.skipped) ? props.proposal.details.skipped as ISeasonTileRow[] : []

        // A few by name: "every tile" skips every tile without a picture, which can be many.
        const named = skipped.slice(0, 4).map((tile) => (tile.reason ? `${tile.title} (${t(`season_tile_skip.${tile.reason}`)})` : tile.title)).join(', ')

        return skipped.length > 4 ? `${named} …` : named
    })

    const text = computed((): string => (isReply.value ? detail('text') : ''))

    const note = computed((): string => (isReply.value ? '' : detail('note')))

    const language = computed((): string => (isReply.value && detail('language') ? languageLabel(detail('language')) : ''))

    const status = computed((): string => (detail('status') ? t(`feedback_status.${detail('status')}`) : ''))

    const icon = computed((): string => {
        switch (props.proposal.kind) {
            case MiloProposalKind.ChannelEnabled:   return 'i-lucide-tv'
            case MiloProposalKind.InvitationActive: return 'i-lucide-party-popper'
            case MiloProposalKind.GuestLinkRotate:  return 'i-lucide-qr-code'
            case MiloProposalKind.RequestAccept:    return 'i-lucide-circle-check'
            case MiloProposalKind.RequestDelivered: return 'i-lucide-package-check'
            case MiloProposalKind.RequestRefuse:    return 'i-lucide-circle-x'
            case MiloProposalKind.CheckIn:          return 'i-lucide-log-in'
            case MiloProposalKind.CheckOut:         return 'i-lucide-log-out'
            case MiloProposalKind.FeedbackReply:    return 'i-lucide-message-square-reply'
            case MiloProposalKind.FeedbackStatus:   return 'i-lucide-message-square-check'
            case MiloProposalKind.LocalEvent:       return 'i-lucide-calendar-heart'
            case MiloProposalKind.RemoveLocalEvent: return 'i-lucide-calendar-x'
            case MiloProposalKind.SeasonalTiles:    return 'i-lucide-sparkles'
            default:                                return 'i-lucide-wand-sparkles'
        }
    })
</script>
